/**
 * Superadmin knobs management page.
 *
 * @module pages/superadmin/knobs
 * @docs-internal Superadmin-only page for inspecting, creating, toggling, and lifecycling runtime knobs.
 */
import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  TextField,
  TagBadge,
  type SelectOption,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useKnobsQuery,
  useUpsertKnobMutation,
  useToggleKnobMutation,
  useSetKnobLifecycleMutation,
  type Knob,
  safeStringify,
} from './useSuperadminData';

const lifecycleOptions = [
  { id: 'active', label: 'Active' },
  { id: 'deprecated', label: 'Deprecated' },
  { id: 'ready_for_cleanup', label: 'Ready for Cleanup' },
] satisfies SelectOption[];

const toLifecycleLabel = (stage: Knob['lifecycleStage']) => {
  switch (stage) {
    case 'deprecated':
      return 'Deprecated';
    case 'ready_for_cleanup':
      return 'Ready for cleanup';
    default:
      return 'Active';
  }
};

/** Insert zero-width spaces after dots so the browser can wrap long dotted keys. */
const wrapOnDots = (key: string) => key.replaceAll('.', '.\u200B');

/**
 * Single-line minified JSON for table rendering. The edit panel still uses
 * `safeStringify` (pretty-printed) \u2014 readability matters when editing, but in
 * the table a 12-line JSON blob blows out row height for no benefit.
 */
const minifyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value);
  }
};

const summarizeKnobValue = (knob: Knob) => {
  switch (knob.valueType) {
    case 'boolean':
      return knob.value === true ? 'true' : 'false';
    case 'string':
      return typeof knob.value === 'string' ? knob.value : '';
    case 'number':
      return typeof knob.value === 'number' && Number.isFinite(knob.value)
        ? knob.value.toString()
        : '';
    case 'json':
      return minifyJson(knob.value);
    default:
      return '';
  }
};

type KnobForm = {
  key: string;
  description: string;
  valueType: Knob['valueType'];
  valueBool: boolean;
  valueString: string;
  valueNumber: string;
  valueJson: string;
  lifecycleStage: Knob['lifecycleStage'];
  finalValueBool: boolean;
  finalValueString: string;
  finalValueNumber: string;
  finalValueJson: string;
};

const emptyKnobForm: KnobForm = {
  key: '',
  description: '',
  valueType: 'boolean',
  valueBool: false,
  valueString: '',
  valueNumber: '',
  valueJson: '{}',
  lifecycleStage: 'active',
  finalValueBool: false,
  finalValueString: '',
  finalValueNumber: '',
  finalValueJson: '{}',
};

export function SuperadminKnobs() {
  const [selectedKnob, setSelectedKnob] = React.useState<Knob | null>(null);
  const [knobForm, setKnobForm] = React.useState<KnobForm>(emptyKnobForm);
  const [knobFormError, setKnobFormError] = React.useState<string | null>(null);
  const [lifecycleError, setLifecycleError] = React.useState<string | null>(null);
  const [showAllLifecycles, setShowAllLifecycles] = React.useState(false);

  const knobsQuery = useKnobsQuery();
  const upsertKnobMutation = useUpsertKnobMutation();
  const toggleKnobMutation = useToggleKnobMutation();
  const setKnobLifecycleMutation = useSetKnobLifecycleMutation();

  React.useEffect(() => {
    if (!selectedKnob || !knobsQuery.data?.knobs) {
      return;
    }
    const fresh = knobsQuery.data.knobs.find((knob) => knob.key === selectedKnob.key);
    if (fresh) {
      setSelectedKnob(fresh);
    }
  }, [selectedKnob, knobsQuery.data]);

  React.useEffect(() => {
    if (selectedKnob) {
      setKnobForm({
        key: selectedKnob.key,
        description: selectedKnob.description ?? '',
        valueType: selectedKnob.valueType,
        valueBool: selectedKnob.valueType === 'boolean' ? Boolean(selectedKnob.value) : false,
        valueString:
          selectedKnob.valueType === 'string' && typeof selectedKnob.value === 'string'
            ? selectedKnob.value
            : '',
        valueNumber:
          selectedKnob.valueType === 'number' && typeof selectedKnob.value === 'number'
            ? String(selectedKnob.value)
            : '',
        valueJson:
          selectedKnob.valueType === 'json' ? safeStringify(selectedKnob.value ?? null) : '{}',
        lifecycleStage: selectedKnob.lifecycleStage,
        finalValueBool:
          selectedKnob.valueType === 'boolean' && typeof selectedKnob.finalValue === 'boolean'
            ? selectedKnob.finalValue
            : false,
        finalValueString:
          selectedKnob.valueType === 'string' && typeof selectedKnob.finalValue === 'string'
            ? selectedKnob.finalValue
            : '',
        finalValueNumber:
          selectedKnob.valueType === 'number' && typeof selectedKnob.finalValue === 'number'
            ? String(selectedKnob.finalValue)
            : '',
        finalValueJson: safeStringify(selectedKnob.finalValue ?? null),
      });
    } else {
      setKnobForm(emptyKnobForm);
    }
  }, [selectedKnob]);

  const handleSaveKnob = () => {
    setKnobFormError(null);
    const key = knobForm.key.trim();
    if (!key) {
      setKnobFormError('Key is required');
      return;
    }

    let value: unknown;
    switch (knobForm.valueType) {
      case 'boolean':
        value = knobForm.valueBool;
        break;
      case 'string':
        value = knobForm.valueString;
        break;
      case 'number': {
        const parsed = Number(knobForm.valueNumber);
        if (Number.isNaN(parsed)) {
          setKnobFormError('Value must be a valid number');
          return;
        }
        value = parsed;
        break;
      }
      case 'json':
        try {
          value = knobForm.valueJson.trim().length ? JSON.parse(knobForm.valueJson) : null;
        } catch (error) {
          setKnobFormError(error instanceof Error ? error.message : 'Invalid JSON');
          return;
        }
        break;
      default:
        setKnobFormError('Unsupported value type');
        return;
    }

    upsertKnobMutation.mutate(
      {
        key,
        description: knobForm.description?.trim() || undefined,
        valueType: knobForm.valueType,
        value,
      },
      {
        onSuccess: (data) => {
          setSelectedKnob(data.knob);
          setKnobFormError(null);
        },
        onError: (error) => {
          setKnobFormError(error instanceof Error ? error.message : 'Failed to save knob');
        },
      }
    );
  };

  const handleSaveLifecycle = () => {
    if (!selectedKnob) {
      setLifecycleError('Select an existing knob first');
      return;
    }

    setLifecycleError(null);

    let finalValue: unknown = undefined;

    if (knobForm.lifecycleStage !== 'active') {
      switch (selectedKnob.valueType) {
        case 'boolean':
          finalValue = knobForm.finalValueBool;
          break;
        case 'string':
          finalValue = knobForm.finalValueString;
          break;
        case 'number': {
          const parsed = Number(knobForm.finalValueNumber);
          if (Number.isNaN(parsed)) {
            setLifecycleError('Final value must be a valid number');
            return;
          }
          finalValue = parsed;
          break;
        }
        case 'json':
          try {
            finalValue = knobForm.finalValueJson.trim().length
              ? JSON.parse(knobForm.finalValueJson)
              : null;
          } catch (error) {
            setLifecycleError(error instanceof Error ? error.message : 'Invalid final JSON');
            return;
          }
          break;
        default:
          setLifecycleError('Unsupported knob type');
          return;
      }
    }

    setKnobLifecycleMutation.mutate(
      {
        key: selectedKnob.key,
        stage: knobForm.lifecycleStage,
        finalValue,
      },
      {
        onSuccess: (data) => {
          setSelectedKnob(data.knob);
          setLifecycleError(null);
        },
        onError: (error) => {
          setLifecycleError(
            error instanceof Error ? error.message : 'Failed to update lifecycle metadata'
          );
        },
      }
    );
  };

  const knobColumns: ColumnDef<StockFeatures, Knob>[] = React.useMemo(
    () => [
      {
        accessorKey: 'key',
        header: 'Key',
        meta: { flex: 3, minWidth: 200 },
        cell: ({ row }) => (
          <Text weight="medium" className="break-words">
            {wrapOnDots(row.original.key)}
          </Text>
        ),
      },
      {
        accessorKey: 'valueType',
        header: 'Type',
        meta: { flex: 1, maxWidth: 100 },
        cell: ({ row }) => <Text size="sm">{row.original.valueType}</Text>,
      },
      {
        accessorKey: 'value',
        header: 'Value',
        meta: { flex: 2, maxWidth: 300 },
        enableSorting: false,
        cell: ({ row }) => (
          <Text size="sm" className="line-clamp-2 whitespace-pre-wrap">
            {summarizeKnobValue(row.original)}
          </Text>
        ),
      },
      {
        accessorKey: 'lifecycleStage',
        header: 'Lifecycle',
        meta: { flex: 1, maxWidth: 140 },
        cell: ({ row }) => (
          <Flex gap="sm" align="center">
            <Text size="sm" className="text-muted-foreground">
              {toLifecycleLabel(row.original.lifecycleStage)}
            </Text>
            {row.original.isStale && <TagBadge name="Stale" color="yellow" hideDot />}
          </Flex>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        meta: { flex: 2, minWidth: 160 },
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : '—'}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Knobs</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Global switches and config values (10s cache).
              </Text>
            </div>
            {knobsQuery.isLoading && <LoadingOrbit size="sm" />}
          </Flex>
        </CardHeader>
        <CardContent>
          {knobsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : knobsQuery.isError ? (
            <Text className="text-destructive">
              {knobsQuery.error instanceof Error
                ? knobsQuery.error.message
                : 'Failed to load knobs'}
            </Text>
          ) : knobsQuery.data?.knobs?.length ? (
            <DataTable
              columns={knobColumns}
              data={
                showAllLifecycles
                  ? knobsQuery.data.knobs
                  : knobsQuery.data.knobs.filter((k) => k.lifecycleStage === 'active')
              }
              onRowClick={(knob) => setSelectedKnob(knob)}
              searchable
              searchPlaceholder="Filter knobs..."
              initialSorting={[{ id: 'key', desc: false }]}
              toolbarContent={
                <Button
                  variant={showAllLifecycles ? 'primary' : 'outline'}
                  size="sm"
                  onPress={() => setShowAllLifecycles((prev) => !prev)}
                >
                  {showAllLifecycles ? 'All lifecycles' : 'Active only'}
                </Button>
              }
            />
          ) : (
            <Text className="text-muted-foreground">No knobs found.</Text>
          )}
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Create or Edit Knob</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Supports boolean, string, number, and JSON values.
              </Text>
            </div>
            <Button
              variant="outline"
              onPress={() => {
                setSelectedKnob(null);
                setKnobForm(emptyKnobForm);
                setKnobFormError(null);
                setLifecycleError(null);
              }}
            >
              New knob
            </Button>
          </Flex>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="md">
            <Flex gap="md" wrap>
              <Box className="min-w-[200px] flex-1">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Key
                </Text>
                <TextField
                  value={knobForm.key}
                  onChange={(value) => setKnobForm((prev) => ({ ...prev, key: value }))}
                  placeholder="agent.truncation.enabled"
                />
              </Box>
              <Box className="min-w-[240px] flex-1">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Description (optional)
                </Text>
                <TextField
                  value={knobForm.description}
                  onChange={(value) => setKnobForm((prev) => ({ ...prev, description: value }))}
                  placeholder="What does this knob control?"
                />
              </Box>
              <Box className="min-w-[160px]">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Type
                </Text>
                <Select
                  items={
                    [
                      { id: 'boolean', label: 'Boolean' },
                      { id: 'string', label: 'String' },
                      { id: 'number', label: 'Number' },
                      { id: 'json', label: 'JSON' },
                    ] satisfies SelectOption[]
                  }
                  selectedKey={knobForm.valueType}
                  onSelectionChange={(value) =>
                    setKnobForm((prev) => ({
                      ...prev,
                      valueType: String(value) as Knob['valueType'],
                    }))
                  }
                />
              </Box>
            </Flex>

            {knobForm.valueType === 'boolean' && (
              <Flex gap="sm" align="center">
                <input
                  type="checkbox"
                  checked={knobForm.valueBool}
                  onChange={(event) =>
                    setKnobForm((prev) => ({ ...prev, valueBool: event.target.checked }))
                  }
                />
                <Text>Enabled</Text>
              </Flex>
            )}

            {knobForm.valueType === 'string' && (
              <Box>
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Value
                </Text>
                <TextField
                  value={knobForm.valueString}
                  onChange={(value) => setKnobForm((prev) => ({ ...prev, valueString: value }))}
                  placeholder="value"
                />
              </Box>
            )}

            {knobForm.valueType === 'number' && (
              <Box>
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Value
                </Text>
                <TextField
                  value={knobForm.valueNumber}
                  onChange={(value) => setKnobForm((prev) => ({ ...prev, valueNumber: value }))}
                  inputMode="decimal"
                  placeholder="0"
                />
              </Box>
            )}

            {knobForm.valueType === 'json' && (
              <Box className="w-full">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  JSON Value
                </Text>
                <textarea
                  className="w-full border border-border rounded-md p-3 text-sm min-h-[160px] font-mono bg-background"
                  value={knobForm.valueJson}
                  onChange={(event) =>
                    setKnobForm((prev) => ({ ...prev, valueJson: event.target.value }))
                  }
                  placeholder="{}"
                />
              </Box>
            )}

            {knobFormError && <Text className="text-destructive text-sm">{knobFormError}</Text>}

            <Flex gap="sm" wrap align="center">
              <Button onPress={handleSaveKnob} isDisabled={upsertKnobMutation.isPending}>
                {upsertKnobMutation.isPending ? 'Saving...' : 'Save Knob'}
              </Button>

              {knobForm.valueType === 'boolean' && knobForm.key.trim().length > 0 && (
                <Button
                  variant="outline"
                  onPress={() =>
                    toggleKnobMutation.mutate(knobForm.key.trim(), {
                      onSuccess: (data) => setSelectedKnob(data.knob),
                    })
                  }
                  isDisabled={toggleKnobMutation.isPending}
                >
                  {toggleKnobMutation.isPending ? 'Toggling...' : 'Toggle'}
                </Button>
              )}

              {selectedKnob && (
                <Text size="sm" className="text-muted-foreground ml-auto">
                  Updated{' '}
                  {selectedKnob.updatedAt ? new Date(selectedKnob.updatedAt).toLocaleString() : '—'}
                  {selectedKnob.updatedBy ? ` by ${selectedKnob.updatedBy}` : ''}
                </Text>
              )}
            </Flex>

            {selectedKnob && (
              <Box className="border-t border-border pt-4 mt-2">
                <Heading level={4} className="text-base mb-2">
                  Lifecycle
                </Heading>
                <Text size="sm" className="text-muted-foreground mb-4">
                  Mark this knob as deprecated or ready for cleanup with a final value.
                </Text>

                <Flex gap="md" wrap align="end">
                  <Box className="min-w-[220px]">
                    <Text size="xs" className="text-muted-foreground mb-1 block">
                      Stage
                    </Text>
                    <Select
                      items={lifecycleOptions}
                      selectedKey={knobForm.lifecycleStage}
                      onSelectionChange={(value) =>
                        setKnobForm((prev) => ({
                          ...prev,
                          lifecycleStage: String(value) as Knob['lifecycleStage'],
                        }))
                      }
                    />
                  </Box>
                </Flex>

                {knobForm.lifecycleStage !== 'active' && (
                  <Box className="mt-4">
                    <Text size="xs" className="text-muted-foreground mb-1 block">
                      Final Value
                    </Text>

                    {selectedKnob.valueType === 'boolean' && (
                      <Flex gap="sm" align="center">
                        <input
                          type="checkbox"
                          checked={knobForm.finalValueBool}
                          onChange={(event) =>
                            setKnobForm((prev) => ({
                              ...prev,
                              finalValueBool: event.target.checked,
                            }))
                          }
                        />
                        <Text>Enabled</Text>
                      </Flex>
                    )}

                    {selectedKnob.valueType === 'string' && (
                      <TextField
                        value={knobForm.finalValueString}
                        onChange={(value) =>
                          setKnobForm((prev) => ({ ...prev, finalValueString: value }))
                        }
                        placeholder="final string value"
                      />
                    )}

                    {selectedKnob.valueType === 'number' && (
                      <TextField
                        value={knobForm.finalValueNumber}
                        onChange={(value) =>
                          setKnobForm((prev) => ({ ...prev, finalValueNumber: value }))
                        }
                        inputMode="decimal"
                        placeholder="0"
                      />
                    )}

                    {selectedKnob.valueType === 'json' && (
                      <textarea
                        className="w-full border border-border rounded-md p-3 text-sm min-h-[140px] font-mono bg-background"
                        value={knobForm.finalValueJson}
                        onChange={(event) =>
                          setKnobForm((prev) => ({ ...prev, finalValueJson: event.target.value }))
                        }
                        placeholder="{}"
                      />
                    )}
                  </Box>
                )}

                {lifecycleError && (
                  <Text className="text-destructive text-sm mt-3">{lifecycleError}</Text>
                )}

                <Flex align="center" justify="between" wrap gap="sm" className="mt-4">
                  <Text size="sm" className="text-muted-foreground">
                    Current stage: {toLifecycleLabel(selectedKnob.lifecycleStage)}
                  </Text>
                  <Button
                    onPress={handleSaveLifecycle}
                    isDisabled={setKnobLifecycleMutation.isPending}
                    variant="outline"
                  >
                    {setKnobLifecycleMutation.isPending ? 'Saving lifecycle...' : 'Save Lifecycle'}
                  </Button>
                </Flex>
              </Box>
            )}
          </Flex>
        </CardContent>
      </Card>
    </Box>
  );
}
